import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import { OPERATE_MQTT } from "../../../../helpers/socketEvent";
import * as url from "../../../../helpers/url_helper";
import { toast } from "react-toastify";
import { useSocket } from "../../../../Utils/socket";
import Request from "../../../../helpers/request";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";

export interface modelType {
  headerTitle: string;
  selectedRows?: any;
  cancleBtnClick(): void;
}

function incrementLastSegment(version: string) {
  const parts = version.split(".").map(Number);

  for (let i = parts.length - 1; i >= 0; i--) {
    parts[i] += 1;

    if (parts[i] < 10) {
      break;
    } else {
      parts[i] = 0;
      if (i === 0) {
        parts.unshift(1);
      }
    }
  }

  return parts.join(".");
}

function replaceUrl(url: string) {
  if (!url) {
    return null;
  }
  return url.replace("https://", "http://");
}

const OtaMultiModel = ({
  headerTitle,
  selectedRows,
  cancleBtnClick,
}: modelType) => {
  const socket: any = useSocket();
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const [firmwareData, setFirmwareData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<any>(null);
  const [selectedFirmwares, setSelectedFirmwares] = useState<
    {
      slave: string;
      hwVersion: string;
      wifiVersion: string;
      firmwareVersion: string;
      binFilePath: string;
    }[]
  >([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const hardwareData = useMemo(() => {
    return selectedRows
      .map((item: any) => ({
        hwVersion: item?.hwVersion,
        product: item?.product,
      }))
      .filter(
        (value: any, index: any, self: any) =>
          index ===
          self.findIndex(
            (t: any) =>
              t.hwVersion === value.hwVersion && t.product === value.product
          )
      );
  }, [selectedRows]);

  const fetchFirmwareList = (hwVersion: any, product: any) => {
    setLoading(true);
    const reqObj = {
      start: 0,
      length: 100,
      search: {
        value: hwVersion || "",
        product: product || "",
        regex: false,
      },
    };

    Request.post(url.GET_FIRMWARE_LIST, reqObj)
      .then((res) => {
        const sortedList = res?.data?.data?.sort((a: any, b: any) => {
          return b.isStable - a.isStable;
        });
        const key = `${hwVersion}-${product}`;
        setFirmwareData((prevLists: any) => ({
          ...prevLists,
          [key]: sortedList,
        }));
        setLoading(false);
      })
      .catch((err) => {
        console.error("err: ", err);
      });
  };

  useEffect(() => {
    if (hardwareData.length > 0) {
      hardwareData.forEach((item: any) => {
        if (item?.hwVersion && item?.product) {
          fetchFirmwareList(item.hwVersion, item.product);
        } else {
          console.log("Skipping fetch: Missing hwVersion or product");
        }
      });
    }
  }, [hardwareData]);
  const handelAllClickOta = () => {
    selectedFirmwares.forEach((fw) => {
      handelClickOta(
        fw.slave,
        replaceUrl(fw.binFilePath),
        incrementLastSegment(fw.wifiVersion)
      );
    });
    toast.success(`OTA sent to successfully`);
  };

  const handelClickOta = (
    slave: string,
    binFilePath: string | null,
    version: string
  ) => {
    if (slave !== undefined) {
      const reqObj = {
        slave: slave,
        tag: `airpanel-${userInfo?.id}`,
        data: [version, binFilePath],
        cmd: "OT",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      toast.success(`OTA sent to slave ${slave}`);
      setIsConfirmModalOpen(null);
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    setIsSubmitted(true);

    const hasErrors = selectedRows.some((item: any) => {
      // const key = `${item.hwVersion}-${item.product}`;
      const selectedRow = selectedFirmwares.find(
        (row) => row.slave === item.slave && row.hwVersion === item.hwVersion
      );
      return !selectedRow?.firmwareVersion;
    });

    if (hasErrors) {
      return;
    }
    setIsConfirmModalOpen(true);
  };
  const renderTableRows = () => {
    const hwVersionGroups: any = {};
    selectedRows?.forEach((item: any) => {
      if (!hwVersionGroups[item.hwVersion]) {
        hwVersionGroups[item.hwVersion] = [];
      }
      hwVersionGroups[item.hwVersion].push(item);
    });

    return selectedRows?.map((item: any, idx: any) => {
      const key = `${item.hwVersion}-${item.product}`;

      const selectedRow = selectedFirmwares.find(
        (row) => row.slave === item.slave && row.hwVersion === item.hwVersion
      );
      const hasError = isSubmitted && !selectedRow?.firmwareVersion;

      const isFirstWithHwVersion = hwVersionGroups[item.hwVersion][0] === item;

      return (
        <tr key={idx} className="hover:bg-gray-100">
          <td className="p-2 border">{item.slave || "-"}</td>

          <td className="p-2 border align-middle">
            <div>{item.hwVersion || "-"}</div>

            {isFirstWithHwVersion && (
              <div className="mt-1">
                <div className="form-check d-flex align-items-center gap-2 text-muted ">
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    onChange={(e) => handleApplyToAllSameHw(e, item)}
                  />
                  <label className="form-check-label mb-0">
                    Apply firmware to all <strong>{item.hwVersion}</strong>{" "}
                    devices
                  </label>
                </div>
              </div>
            )}
          </td>

          <td className="p-2 border">{item.wifiVersion || "-"}</td>
          {loading ? (
            <td
              className="p-2 border text-center align-middle"
              style={{ verticalAlign: "middle" }}
            >
              <div className="d-flex justify-content-center align-items-center">
                <Spinner style={{ width: "1.5rem", height: "1.5rem" }} />
              </div>
            </td>
          ) : (
            <td className="p-2 border">
              {hasError && (
                <div
                  className="text-danger mb-1"
                  style={{ fontSize: "0.875rem" }}
                >
                  * Please select a firmware
                </div>
              )}
              <select
                className="form-select"
                onChange={(e) => handleSelectChange(e, item)}
                value={selectedRow?.firmwareVersion || ""}
              >
                <option value="" disabled>
                  Select a firmware
                </option>
                {firmwareData[key]?.map((firmware: any, index: any) => (
                  <option key={index} value={firmware.firmwareVersion}>
                    {`${firmware.isStable ? "🟢" : "🔴"} ${
                      firmware.product
                    } - ${firmware.hardwareVersion} - Firmware Version ${
                      firmware.firmwareVersion
                    }`}
                  </option>
                ))}
              </select>
            </td>
          )}
        </tr>
      );
    });
  };

  const handleApplyToAllSameHw = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: any
  ) => {
    if (!event.target.checked) return;

    const key = `${item.hwVersion}-${item.product}`;

    const selectedRow = selectedFirmwares.find(
      (row) => row.slave === item.slave && row.hwVersion === item.hwVersion
    );

    if (!selectedRow?.firmwareVersion) {
      toast.error(
        "Please select a firmware version first before applying to all devices"
      );
      event.target.checked = false;
      return;
    }

    const sameHwItems = selectedRows.filter(
      (row: any) => row.hwVersion === item.hwVersion
    );

    const firmwareList = firmwareData[key] || [];
    const selectedFirmware = firmwareList.find(
      (fw: any) => fw.firmwareVersion === selectedRow.firmwareVersion
    );

    if (!selectedFirmware) return;

    setSelectedFirmwares((prev) => {
      const updated = [...prev];

      const filtered = updated.filter(
        (row) => row.hwVersion !== item.hwVersion
      );

      const newEntries = sameHwItems.map((hwItem: any) => ({
        slave: hwItem.slave,
        hwVersion: hwItem.hwVersion,
        wifiVersion: hwItem.wifiVersion,
        firmwareVersion: selectedFirmware.firmwareVersion,
        binFilePath: selectedFirmware.binFilePath,
      }));

      return [...filtered, ...newEntries];
    });
  };

  const handleSelectChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    item: any
  ) => {
    const selectedVersion = event.target.value;
    const key = `${item.hwVersion}-${item.product}`;

    const firmwareList = firmwareData[key] || [];

    const selectedFirmware = firmwareList.find(
      (fw: any) => fw.firmwareVersion === selectedVersion
    );

    if (!selectedFirmware) return;

    const newEntry = {
      slave: item.slave,
      hwVersion: item.hwVersion,
      wifiVersion: item.wifiVersion,
      firmwareVersion: selectedFirmware.firmwareVersion,
      binFilePath: selectedFirmware.binFilePath,
    };

    setSelectedFirmwares((prev) => {
      const existingIndex = prev.findIndex(
        (row) => row.slave === item.slave && row.hwVersion === item.hwVersion
      );

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = newEntry;
        return updated;
      } else {
        return [...prev, newEntry];
      }
    });
  };
  return (
    <div>
      {isConfirmModalOpen && (
        <ConfirmationModal
          headerTitle="Confirm OTA?"
          text="Are you sure you want to update the firmware on this device? This action will apply the selected OTA version and cannot be undone."
          buttonText="OTA"
          onClick={() => {
            handelAllClickOta();
          }}
          cancleBtnClick={() => setIsConfirmModalOpen(null)}
          loading={loading}
        />
      )}
      <Modal isOpen={true} className="modal-xl" centered>
        <div className="modal-header justify-content-between">
          <div className="d-flex flex-column align-items-center">
            <h5
              className="modal-title"
              id="staticBackdropLabel"
              style={{ textAlign: "center" }}
            >
              {headerTitle}
            </h5>
          </div>
        </div>

        <ModalBody style={{ padding: 0 }}>
          <div
            style={{
              // maxHeight: "70vh",
              overflowY: "auto",
              overflowX: "auto",
              backgroundColor: "#F7F7FA",
              padding: "20px",
            }}
          >
            <Card>
              <CardBody>
                <div
                  style={{
                    maxHeight: "70vh",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover table-sm ">
                    {/* className="sticky-top" */}
                    <thead>
                      <tr>
                        <th
                          style={{
                            minWidth: "150px",
                            backgroundColor: "#EFF2F7",
                          }}
                          className="p-2 border"
                        >
                          Slave
                        </th>
                        <th
                          style={{
                            minWidth: "80px",
                            backgroundColor: "#EFF2F7",
                          }}
                          className="p-2 border"
                        >
                          Hardware Version
                        </th>
                        <th
                          style={{
                            minWidth: "80px",
                            backgroundColor: "#EFF2F7",
                          }}
                          className="p-2 border"
                        >
                          Firmware Version
                        </th>
                        <th
                          style={{
                            minWidth: "300px",
                            backgroundColor: "#EFF2F7",
                          }}
                          className="p-2 border"
                        >
                          Update
                        </th>
                      </tr>
                    </thead>
                    <tbody>{renderTableRows()}</tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            className="btn btn-outline-secondary"
            onClick={cancleBtnClick}
          >
            Cancel
          </button>

          <button
            className="btn btn-outline-secondary"
            onClick={handleSubmit}
            disabled={loading}
          >
            OTA
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OtaMultiModel;
